import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import HelpButton from '../tours/HelpButton';

const NAVY   = '#0C2D48';
const LIME   = '#E4E69B';
const FONT   = "'Raleway', sans-serif";
const POLL_MS = 5000;

function initials(name) {
  return (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// Stable colour from name string when no stored colour available
const PALETTE = ['#5352ed','#ff6b81','#2ed573','#ffa502','#1e90ff','#ff4757','#7bed9f','#eccc68'];
function nameColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function formatTime(isoStr) {
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs  = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1)  return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs  < 24) return `${diffHrs}h ago`;
  if (diffDays < 7)  return `${diffDays}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function FamilyChat() {
  const { currentUser } = useCurrentUser();
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState('');
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const loadMessages = useCallback(() => {
    api('/api/family-chat')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.messages) setMessages(d.messages); })
      .catch(() => {});
  }, []);

  // Initial load + 5s poll
  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, POLL_MS);
    return () => clearInterval(t);
  }, [loadMessages]);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      const res = await api('/api/family-chat', {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error('Failed to send');
      setInput('');
      loadMessages();
    } catch {
      setError('Could not send message. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: 'calc(100vh - 120px)', maxWidth: 700,
      margin: '0 auto', fontFamily: FONT,
    }}>
      {/* Header */}
      <div style={{
        padding: '1rem 1.25rem 0.85rem',
        borderBottom: '1px solid #e8e8e8',
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        background: '#fff', flexShrink: 0,
      }}>
        <span style={{ fontSize: '1.4rem' }}>💬</span>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: NAVY }}>Family Chat</h2>
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#888' }}>Household members with accepted invites</p>
        </div>
        <HelpButton tourId="familychat" />
      </div>

      {/* Messages */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '1rem 1.25rem',
        background: '#f8f9fb',
        display: 'flex', flexDirection: 'column', gap: '0.75rem',
      }}>
        {messages.length === 0 ? (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: '#bbb', textAlign: 'center', paddingTop: '3rem',
          }}>
            <span style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👋</span>
            <p style={{ margin: 0, fontWeight: 600, color: '#aaa' }}>No messages yet</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#bbb' }}>Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.senderUserId === currentUser?.id;
            const avatarColor = msg.senderColor || nameColor(msg.senderName);
            return (
              <div key={msg.id} style={{
                display: 'flex',
                flexDirection: isMe ? 'row-reverse' : 'row',
                alignItems: 'flex-end', gap: '0.5rem',
              }}>
                {/* Avatar */}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: avatarColor, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  {initials(msg.senderName)}
                </div>
                {/* Bubble */}
                <div style={{ maxWidth: '68%' }}>
                  {!isMe && (
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '0.2rem', paddingLeft: '0.25rem' }}>
                      {msg.senderName}
                    </div>
                  )}
                  <div style={{
                    padding: '0.6rem 0.9rem',
                    borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: isMe ? NAVY : '#fff',
                    color: isMe ? LIME : '#1a1a2e',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    fontSize: '0.9rem', lineHeight: 1.45,
                    wordBreak: 'break-word',
                  }}>
                    {msg.body}
                  </div>
                  <div style={{
                    fontSize: '0.68rem', color: '#bbb', marginTop: '0.2rem',
                    textAlign: isMe ? 'right' : 'left', paddingLeft: '0.25rem', paddingRight: '0.25rem',
                  }}>
                    {formatTime(msg.createdAt)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '0.5rem 1.25rem', background: '#fff5f5', color: '#c0392b', fontSize: '0.82rem', flexShrink: 0 }}>
          {error}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} style={{
        display: 'flex', gap: '0.5rem',
        padding: '0.75rem 1.25rem',
        borderTop: '1px solid #e8e8e8',
        background: '#fff', flexShrink: 0,
      }}>
        <input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Message the family…"
          disabled={sending}
          style={{
            flex: 1, padding: '0.6rem 0.9rem',
            border: '1px solid #dde3e8', borderRadius: 10,
            fontSize: '0.9rem', fontFamily: FONT,
            outline: 'none', background: '#f8f9fb',
            color: NAVY,
          }}
          onFocus={e => { e.target.style.borderColor = NAVY; e.target.style.background = '#fff'; }}
          onBlur={e =>  { e.target.style.borderColor = '#dde3e8'; e.target.style.background = '#f8f9fb'; }}
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          style={{
            padding: '0.6rem 1.2rem',
            background: input.trim() && !sending ? NAVY : '#ccc',
            color: input.trim() && !sending ? LIME : '#fff',
            border: 'none', borderRadius: 10,
            fontSize: '0.88rem', fontWeight: 700,
            cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
            fontFamily: FONT, flexShrink: 0,
            transition: 'background 0.15s',
          }}
        >
          {sending ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}
